import type { Square, PieceSymbol, Color } from './chessEngine'
import { computeOptimalCaptureMoves, getValidMoveSquares } from './chessEngine'
import { shuffle } from '../utils'

export type ChessMode       = 'recognize' | 'movement' | 'capture' | 'puzzle'
export type ChessDifficulty = 'easy' | 'medium' | 'hard'

// ── Piece data ────────────────────────────────────────────────

export const PIECE_SYMBOLS: Record<string, Record<PieceSymbol, string>> = {
  w: { k: '♔', q: '♕', r: '♖', b: '♗', n: '♘', p: '♙' },
  b: { k: '♚', q: '♛', r: '♜', b: '♝', n: '♞', p: '♟' },
}

// Piece types included in each difficulty pool for Recognize mode
export const RECOGNIZE_POOL: Record<ChessDifficulty, PieceSymbol[]> = {
  easy:   ['k', 'q', 'r', 'b'],
  medium: ['k', 'q', 'r', 'b', 'n', 'p'],
  hard:   ['k', 'q', 'r', 'b', 'n', 'p'],
}

// In hard mode we include both colors
export const RECOGNIZE_COLORS: Record<ChessDifficulty, Color[]> = {
  easy:   ['w'],
  medium: ['w'],
  hard:   ['w', 'b'],
}

// ── FEN builder ───────────────────────────────────────────────

const FILES = 'abcdefgh'

export function buildFen(
  pieces: { square: Square; type: PieceSymbol; color: Color }[],
  turn: Color = 'w',
): string {
  // 8x8 grid indexed [rank 8..1][file a..h]
  const grid: (string | null)[][] = Array.from({ length: 8 }, () => Array(8).fill(null))

  for (const { square, type, color } of pieces) {
    const file = FILES.indexOf(square[0])
    const rank = parseInt(square[1]) - 1
    grid[7 - rank][file] = color === 'w' ? type.toUpperCase() : type.toLowerCase()
  }

  const rows = grid.map((row) => {
    let str = ''; let empty = 0
    for (const cell of row) {
      if (cell === null) { empty++ } else { if (empty) { str += empty; empty = 0 } str += cell }
    }
    if (empty) str += empty
    return str
  })

  return `${rows.join('/')} ${turn} - - 0 1`
}

// ── All valid squares ─────────────────────────────────────────

export const ALL_SQUARES: Square[] = []
for (const f of 'abcdefgh') for (let r = 1; r <= 8; r++) ALL_SQUARES.push(`${f}${r}` as Square)

function randomSquare(exclude: Square[] = []): Square {
  const pool = ALL_SQUARES.filter((s) => !exclude.includes(s))
  return pool[Math.floor(Math.random() * pool.length)]
}

function centerSquares(): Square[] {
  return ['c3','c4','c5','c6','d3','d4','d5','d6','e3','e4','e5','e6','f3','f4','f5','f6'] as Square[]
}

// ── Recognize mode question generation ───────────────────────

export interface RecognizeQuestion {
  pieceType:  PieceSymbol
  color:      Color
  symbol:     string
  options:    PieceSymbol[]   // 4 choices (piece types)
}

const ALL_PIECE_TYPES: PieceSymbol[] = ['k', 'q', 'r', 'b', 'n', 'p']

function buildOptions(correct: PieceSymbol, pool: PieceSymbol[]): PieceSymbol[] {
  const distractors = shuffle(pool.filter((p) => p !== correct)).slice(0, 3)
  return shuffle([correct, ...distractors])
}

export function generateRecognizeQuestions(
  difficulty: ChessDifficulty,
  count = 10,
): RecognizeQuestion[] {
  const piecePool  = RECOGNIZE_POOL[difficulty]
  const colorPool  = RECOGNIZE_COLORS[difficulty]
  const optionPool = difficulty === 'hard' ? ALL_PIECE_TYPES : piecePool

  const questions: RecognizeQuestion[] = []
  for (let i = 0; i < count; i++) {
    const pieceType = piecePool[Math.floor(Math.random() * piecePool.length)]
    const color     = colorPool[Math.floor(Math.random() * colorPool.length)]
    questions.push({
      pieceType,
      color,
      symbol:  PIECE_SYMBOLS[color][pieceType],
      options: buildOptions(pieceType, optionPool),
    })
  }
  return questions
}

// ── Movement mode position generation ────────────────────────

export interface MovementPosition {
  fen:          string
  targetSquare: Square
  pieceType:    PieceSymbol
  validSquares: Square[]  // precomputed correct answer
}

const MOVEMENT_PIECES: Record<ChessDifficulty, PieceSymbol[]> = {
  easy:   ['r', 'q'],
  medium: ['n', 'b', 'r'],
  hard:   ['n', 'b', 'r', 'q'],
}

const BLOCKER_COUNT: Record<ChessDifficulty, number> = {
  easy:   0,
  medium: 2,
  hard:   4,
}

export function generateMovementPosition(difficulty: ChessDifficulty): MovementPosition {
  const pieceTypes = MOVEMENT_PIECES[difficulty]
  const pieceType  = pieceTypes[Math.floor(Math.random() * pieceTypes.length)]

  const centerPool  = centerSquares()
  const targetSquare = centerPool[Math.floor(Math.random() * centerPool.length)]

  const placed: Square[] = [targetSquare]
  const pieces: { square: Square; type: PieceSymbol; color: Color }[] = [
    { square: targetSquare, type: pieceType, color: 'w' },
    { square: randomSquare([...placed, ...centerPool.slice(0, 8)]), type: 'k', color: 'w' },
    { square: randomSquare([...placed, ...centerPool.slice(0, 8)]), type: 'k', color: 'b' },
  ]

  const numBlockers = BLOCKER_COUNT[difficulty]
  for (let i = 0; i < numBlockers; i++) {
    const sq = randomSquare(pieces.map((p) => p.square))
    placed.push(sq)
    const isCaptureable = difficulty === 'hard' && Math.random() < 0.5
    pieces.push({ square: sq, type: 'p', color: isCaptureable ? 'b' : 'w' })
  }

  const fen = buildFen(pieces, 'w')

  const validSquares = getValidMoveSquares(fen, targetSquare)

  return { fen, targetSquare, pieceType, validSquares }
}

// ── Capture mode position generation ─────────────────────────

export interface CapturePosition {
  fen:            string
  controlSquare:  Square
  pieceType:      PieceSymbol
  pawnSquares:    Square[]
  optimalMoves:   number
}

const CAPTURE_PIECES: Record<ChessDifficulty, PieceSymbol[]> = {
  easy:   ['r', 'q'],
  medium: ['n', 'b'],
  hard:   ['n', 'b', 'r', 'q'],
}

const PAWN_COUNTS: Record<ChessDifficulty, number> = {
  easy:   3,
  medium: 4,
  hard:   7,
}

export function generateCapturePosition(difficulty: ChessDifficulty): CapturePosition {
  const pieceTypes  = CAPTURE_PIECES[difficulty]
  const pieceType   = pieceTypes[Math.floor(Math.random() * pieceTypes.length)]
  const pawnCount   = PAWN_COUNTS[difficulty]

  const controlSquare = randomSquare()
  const placed: Square[] = [controlSquare]

  const pawnSquares: Square[] = []
  for (let i = 0; i < pawnCount; i++) {
    const sq = randomSquare(placed)
    placed.push(sq)
    pawnSquares.push(sq)
  }

  const pieces: { square: Square; type: PieceSymbol; color: Color }[] = [
    { square: controlSquare, type: pieceType, color: 'w' },
    ...pawnSquares.map((sq) => ({ square: sq, type: 'p' as PieceSymbol, color: 'b' as Color })),
    { square: randomSquare(placed), type: 'k', color: 'w' },
    { square: randomSquare(placed), type: 'k', color: 'b' },
  ]

  const fen          = buildFen(pieces, 'w')
  const optimalMoves = computeOptimalCaptureMoves(pieceType, controlSquare, pawnSquares)

  return { fen, controlSquare, pieceType, pawnSquares, optimalMoves }
}
